import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { financialAPI, Income } from '../services/api';

const IncomeManager: React.FC = () => {
  const [income, setIncome] = useState<Income[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIncome, setEditingIncome] = useState<Income | null>(null);
  const [formData, setFormData] = useState({
    source_name: '',
    amount: '',
    frequency: 'monthly' as 'monthly' | 'weekly' | 'daily' | 'yearly',
  });

  useEffect(() => {
    loadIncome();
  }, []);

  const loadIncome = async () => {
    try {
      setLoading(true);
      const data = await financialAPI.getIncome();
      setIncome(data);
      setError(null);
    } catch (err) {
      setError('Failed to load income sources');
      console.error('Income load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (incomeItem?: Income) => {
    if (incomeItem) {
      setEditingIncome(incomeItem);
      setFormData({
        source_name: incomeItem.source_name,
        amount: incomeItem.amount.toString(),
        frequency: incomeItem.frequency,
      });
    } else {
      setEditingIncome(null);
      setFormData({
        source_name: '',
        amount: '',
        frequency: 'monthly',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingIncome(null);
  };

  const handleSubmit = async () => {
    try {
      const incomeData = {
        source_name: formData.source_name,
        amount: parseFloat(formData.amount),
        frequency: formData.frequency,
      };

      if (editingIncome) {
        await financialAPI.updateIncome({ ...incomeData, id: editingIncome.id! });
      } else {
        await financialAPI.createIncome(incomeData);
      }

      handleCloseDialog();
      loadIncome();
    } catch (err) {
      console.error('Submit error:', err);
      setError('Failed to save income source');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this income source?')) {
      try {
        await financialAPI.deleteIncome(id);
        loadIncome();
      } catch (err) {
        console.error('Delete error:', err);
        setError('Failed to delete income source');
      }
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getMonthlyEquivalent = (amount: number, frequency: string) => {
    switch (frequency) {
      case 'weekly':
        return amount * 4.33;
      case 'daily':
        return amount * 30;
      case 'yearly':
        return amount / 12;
      default:
        return amount;
    }
  };

  const getTotalMonthlyIncome = () => {
    return income.reduce((total, item) => total + getMonthlyEquivalent(item.amount, item.frequency), 0);
  };

  const getFrequencyColor = (frequency: string) => {
    switch (frequency) {
      case 'daily':
        return 'success';
      case 'weekly':
        return 'info';
      case 'monthly':
        return 'primary';
      case 'yearly':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Income Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Income Source
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Total Monthly Income
          </Typography>
          <Typography variant="h4" color="success.main">
            {formatCurrency(getTotalMonthlyIncome())}
          </Typography>
        </CardContent>
      </Card>

      {/* Income Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Frequency</TableCell>
              <TableCell>Monthly Equivalent</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {income.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.source_name}</TableCell>
                <TableCell>{formatCurrency(item.amount)}</TableCell>
                <TableCell>
                  <Chip
                    label={item.frequency}
                    color={getFrequencyColor(item.frequency) as any}
                  />
                </TableCell>
                <TableCell>
                  {formatCurrency(getMonthlyEquivalent(item.amount, item.frequency))}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenDialog(item)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(item.id!)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingIncome ? 'Edit Income Source' : 'Add New Income Source'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Source Name"
              value={formData.source_name}
              onChange={(e) => setFormData({ ...formData, source_name: e.target.value })}
            />

            <TextField
              fullWidth
              margin="normal"
              label="Amount"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Frequency</InputLabel>
              <Select
                value={formData.frequency}
                label="Frequency"
                onChange={(e) => setFormData({ ...formData, frequency: e.target.value as any })}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingIncome ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IncomeManager;
